/**
 * BrandedLoginPage — resolves a tenant's branded-login slug (e.g.
 * /cloverleaf) to its public branding and renders the standard login
 * themed with that tenant's logo/app name. Auth is still by email; the
 * slug only supplies branding. Unknown/invalid slug falls back to the
 * normal (unbranded) login so tenants can't be enumerated.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { applyBrandingToDOM } from '@/lib/branding';
import LoginPage from '@/pages/LoginPage';

export default function BrandedLoginPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, branding: null, notFound: false });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const b = await api.getBrandedLogin(slug);
        if (!alive) return;
        // Retired slug → redirect to the tenant's current branded URL.
        if (b && b.redirect_to) {
          setState({ loading: false, branding: null, notFound: false, redirectTo: b.redirect_to });
          return;
        }
        // The login card sits on a dark background — prefer the dark logo.
        const logo = b.logo_url_dark || b.logo_url || '';
        applyBrandingToDOM(b);
        setState({
          loading: false,
          notFound: false,
          rootPage: b.root_page || '',
          branding: {
            logo, app_name: b.app_name || '', tagline: b.login_tagline || '',
            bgImage: b.login_bg_image_url || '', slug, primary: b.primary_color || '',
          },
        });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, branding: null, notFound: true });
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950" data-testid="branded-login-loading">
        <div className="w-8 h-8 rounded-full border-2 border-blue-500/40 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  // Unknown/reserved slug → normal login (no tenant leak).
  if (state.notFound) return <Navigate to="/login" replace />;

  // Retired slug → send the visitor to the current branded URL.
  if (state.redirectTo) return <Navigate to={`/${state.redirectTo}`} replace />;

  // Per-tenant root page: if this tenant configured its branded link to show
  // the landing page, honor it; otherwise (default) render the login card.
  if (state.rootPage === 'landing') return <Navigate to="/welcome" replace />;

  return <LoginPage branding={state.branding} />;
}
